import type { Breakpoint, SxProps } from '@mui/material';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { useTranslation } from 'react-i18next';

export enum ConfirmButtonColors {
  primary = 'primary',
  destructive = 'destructive',
}

export enum CancelButtonColors {
  primary = 'primary',
  destructive = 'destructive',
}

export enum ButtonVariants {
  tonal = 'tonal',
  outlined = 'outlined',
  filled = 'filled',
  text = 'text',
}

export const ConfirmationModal = ({
  onConfirm,
  onCancel,
  children,
  title,
  open,
  bodyText,
  confirmButtonLabel,
  cancelButtonLabel,
  deleteButtonLabel,
  maxWidth = 'sm',
  loading = false,
  dialogStyles,
  confirmButtonColor = ConfirmButtonColors.destructive,
  cancelButtonColor,
  cancelButtonVariant = ButtonVariants.outlined,
  confirmButtonVariant = ButtonVariants.filled,
  deleteButtonVariant = ButtonVariants.outlined,
  showCancelButton = true,
  onDelete,
  showDeleteButton,
  testIdPrefix = 'confirmation-modal',
  confirmButtonDisabled = false,
  disableCancelButton = loading,
}: {
  onConfirm: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  title: string;
  open: boolean;
  bodyText?: string;
  children?: React.ReactNode;
  confirmButtonLabel?: string;
  cancelButtonLabel?: string;
  deleteButtonLabel?: string;
  maxWidth?: Breakpoint | false;
  loading?: boolean;
  dialogStyles?: {
    dialogContainer?: SxProps;
    dialogContentStyles?: SxProps;
    dialogTitleStyles?: SxProps;
    confirmButton?: SxProps;
    cancelButton?: SxProps;
  };
  confirmButtonColor?: ConfirmButtonColors;
  cancelButtonColor?: CancelButtonColors;
  showCancelButton?: boolean;
  showDeleteButton?: boolean;
  cancelButtonVariant?: ButtonVariants;
  confirmButtonVariant?: ButtonVariants;
  deleteButtonVariant?: ButtonVariants;
  testIdPrefix?: string;
  confirmButtonDisabled?: boolean;
  disableCancelButton?: boolean;
}) => {
  const { t } = useTranslation('shared');
  return (
    <Dialog
      data-testid={`${testIdPrefix}-dialog`}
      onClose={onCancel}
      open={open}
      maxWidth={maxWidth}
      fullWidth
      sx={[
        ...(dialogStyles?.dialogContainer &&
        Array.isArray(dialogStyles?.dialogContainer)
          ? dialogStyles.dialogContainer
          : [dialogStyles?.dialogContainer]),
      ]}
    >
      <DialogTitle data-testid={`${testIdPrefix}-title`}>{title}</DialogTitle>

      {(children || bodyText) && (
        <DialogContent
          sx={[
            { minWidth: '400px' },
            ...(dialogStyles?.dialogContentStyles &&
            Array.isArray(dialogStyles?.dialogContentStyles)
              ? dialogStyles.dialogContentStyles
              : [dialogStyles?.dialogContentStyles]),
          ]}
          data-testid={`${testIdPrefix}-content`}
        >
          {children}
          {!children && bodyText && (
            <Typography
              data-testid={`${testIdPrefix}-body-text`}
              variant='bodyLarge'
            >
              {bodyText}
            </Typography>
          )}
        </DialogContent>
      )}

      <DialogFooter data-testid={`${testIdPrefix}-footer`}>
        {showCancelButton && (
          <Button
            type='button'
            variant={cancelButtonVariant}
            color={cancelButtonColor}
            disabled={disableCancelButton}
            onClick={onCancel}
            label={cancelButtonLabel ?? t('shared:cancel')}
            dataTestId={`${testIdPrefix}-cancel-button`}
          />
        )}

        {showDeleteButton && (
          <Button
            type='button'
            variant={deleteButtonVariant}
            color={cancelButtonColor}
            disabled={loading}
            onClick={onDelete}
            label={deleteButtonLabel ?? t('shared:delete')}
            dataTestId={`${testIdPrefix}-delete-button`}
          />
        )}

        <Button
          data-testid={`${testIdPrefix}-confirm-button`}
          sx={{ ml: 1 }}
          type='button'
          variant={confirmButtonVariant}
          color={confirmButtonColor}
          disabled={loading || confirmButtonDisabled}
          onClick={onConfirm}
          label={confirmButtonLabel ?? t('shared:confirm')}
          dataTestId={`${testIdPrefix}-confirm-button`}
        />
      </DialogFooter>
    </Dialog>
  );
};

export default ConfirmationModal;
